import React from 'react';
import { motion } from 'framer-motion';

const Logo = ({ size = 'large', className = '' }) => {
  const sizes = {
    small: { width: 120, height: 40, fontSize: 'text-xl' },
    medium: { width: 180, height: 60, fontSize: 'text-3xl' },
    large: { width: 240, height: 80, fontSize: 'text-4xl' },
    xlarge: { width: 320, height: 100, fontSize: 'text-6xl' }
  };

  const currentSize = sizes[size];

  return (
    <motion.div 
      className={`flex items-center justify-center ${className}`}
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <div className="relative">
        {/* SVG Logo */}
        <svg
          width={currentSize.width}
          height={currentSize.height}
          viewBox="0 0 240 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="relative z-10"
        >
          {/* Background Glow */}
          <defs>
            <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="50%" stopColor="#EC4899" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>
            
            <linearGradient id="heartGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#EC4899" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>

            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge> 
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* Main Text */}
          <text
            x="120"
            y="45"
            textAnchor="middle"
            className="font-black"
            style={{ 
              fontSize: '28px',
              fill: 'url(#logoGradient)',
              filter: 'url(#glow)'
            }}
          >
            datenite
          </text>

          {/* Heart Icon */}
          <motion.g
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <path
              d="M200 25c0-5.5 4.5-10 10-10s10 4.5 10 10c0 5.5-4.5 10-10 10s-10-4.5-10-10z"
              fill="url(#heartGradient)"
              opacity="0.8"
            />
            <path
              d="M210 20c-2.5-2.5-6.5-2.5-9 0-2.5-2.5-6.5-2.5-9 0-2.5 2.5-2.5 6.5 0 9l9 9 9-9c2.5-2.5 2.5-6.5 0-9z"
              fill="white"
              transform="translate(200, 15) scale(0.8)"
            />
          </motion.g>

          {/* Video Icon */}
          <motion.g
            animate={{ 
              opacity: [0.6, 1, 0.6],
              y: [0, -2, 0]
            }}
            transition={{ 
              duration: 3, 
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
          >
            <rect
              x="15"
              y="20"
              width="16"
              height="12"
              rx="2"
              fill="url(#logoGradient)"
              opacity="0.8"
            />
            <polygon
              points="32,23 32,29 38,26"
              fill="white"
            />
            <circle cx="20" cy="24" r="1.5" fill="white" opacity="0.8" />
            <circle cx="26" cy="24" r="1.5" fill="white" opacity="0.8" />
          </motion.g>

          {/* Decorative Stars */}
          <motion.circle
            cx="50"
            cy="15"
            r="1.5"
            fill="white"
            opacity="0.6"
            animate={{ 
              opacity: [0.3, 1, 0.3],
              scale: [1, 1.3, 1]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity,
              delay: 0.5
            }}
          />
          
          <motion.circle
            cx="180"
            cy="60"
            r="1"
            fill="white"
            opacity="0.4"
            animate={{ 
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.5, 1]
            }}
            transition={{ 
              duration: 2.5, 
              repeat: Infinity,
              delay: 1.5
            }}
          />

          {/* Tagline */}
          <text
            x="120"
            y="65"
            textAnchor="middle"
            className="text-xs"
            fill="rgba(255, 255, 255, 0.7)"
            style={{ fontSize: '10px' }}
          >
            Video Dating Revolution
          </text>
        </svg>

        {/* Animated Background Glow */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-romantic-purple via-romantic-pink to-yellow-400 rounded-full opacity-20 blur-xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.1, 0.3, 0.1]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            width: currentSize.width + 40,
            height: currentSize.height + 20,
            left: -20,
            top: -10
          }}
        />
      </div>
    </motion.div>
  );
};

export default Logo;
